import React from "react";
import { Row, Col } from "react-bootstrap";
import {
  Navigation,
  Page,
  Storage,
  TaskSorting,
  TaskImageClick,
  TaskMultiChoice,
  Image,
} from "@bittnerdevelopmentgroup/interactive";

import "./styles.css"

import av1 from "./assets/PrinEco4_PriceDiscrimination_Airplane_v1.jpg"
import av2 from "./assets/PrinEco4_PriceDiscrimination_Airplane_v2.jpg"
import av3 from "./assets/PrinEco4_PriceDiscrimination_Airplane_v3.jpg"
import av4 from "./assets/PrinEco4_PriceDiscrimination_Airplane_v4.jpg"
import panel6 from "./assets/PrinEco4_PriceDiscrimination_p6.jpg"
import panel6a from "./assets/PrinEco4_PriceDiscrimination_p6-1.jpg"
import panel6b from "./assets/PrinEco4_PriceDiscrimination_p6-2.jpg"

class PriceDiscrimination extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="priceDiscrimination">
        <div className="sr-only" aria-hidden="true">
          {[av1, av2, av3, av4, panel6].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-11">
          <Page>
            <p>
              A plane is flying from San Diego to New York City. Three passengers on that plane—Angel, Hanas, and Lee—are all sitting in aisle seats in the same section. However, each passenger has different timelines when it comes to ticket purchases for the same flight, and those timelines impact the price elasticity of their demand.
            </p>
            <p>
              The following are the details of each passenger's ticket purchase. <b>Which passengers have relatively elastic demand, and which have relatively inelastic demand for their plane tickets?</b>
            </p>
            <p>
              <i>Sort each of the following scenarios into the appropriate demand category.</i>
            </p>
            <TaskSorting
              state={Storage()}
              feedbackCorrect="The more time a passenger has to purchase a plane ticket, the more price elastic their individual demand."
              feedbackIncorrect="Think about the amount of time that each passenger has to find a low-priced ticket."
              prompt={<></>}
              categories={[
                "Relatively inelastic",
                "Relatively elastic"
              ]}
            >
              <TaskSorting.Option
                category={1}
                feedback={[
                  undefined,
                  "Angel had many choices and time to find an inexpensive ticket price. This means he has relatively elastic demand for the plane ticket."
                ]}
              >
                Angel is going to a wedding in New York City. He bought his ticket nine months ago.
              </TaskSorting.Option>
              <TaskSorting.Option
                category={0}
                feedback={[
                  "Hanas had very few choices and very little time to find an inexpensive ticket price. This means she has relatively inelastic demand for the plane ticket.",
                  undefined,
                ]}
              >
                Hanas has been called to an emergency meeting of her advertising firm in New York City. She was in a huge rush and couldn't miss this important meeting, so she bought her ticket just 3 hours before take-off.
              </TaskSorting.Option>
              <TaskSorting.Option
                category={0}
                feedback={[
                  "Lee had few choices because he changed his itinerary. This means he has relatively inelastic demand for the plane ticket.",
                  undefined,
                ]}
              >
                Lee's meeting in New York City was postponed by three days. He did not want to stay in New York for those extra days, so he decides to find an earlier flight. Because of this change, Lee had to exchange his original ticket for a new ticket a few days before the flight.
              </TaskSorting.Option>
            </TaskSorting>
          </Page>
          <Page>
            <p>
              Here is the section of the plane where Angel, Lee, and Hanas are sitting. The image shows that these passengers paid different prices based on the elasticity of their demand for a ticket. This is an example of <b>price discrimination</b>.
            </p>
            <Image
              width="100%"
              src={av1}
              photoCredit={"chain45154/Getty Images"}
              aria-labelledby="task-image-click-image"
              alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, $425, $600, and $250."
            />
          </Page>
          <Page>
            <p>
              Angel is going to a wedding in New York City, and he bought his ticket nine months ago.
            </p>
            <p>
              <b>If the average price of a ticket on this plane is $300, what do you think Angel paid for his ticket?</b>
            </p>
            <p>
              <i>Select the price that Angel paid in the following image.</i>
            </p>
            <TaskImageClick
              state={Storage()}
              style={{
                paddingBottom: "1em"
              }}
              hotspots={[
                {
                  correct: false,
                  label: "300 dollars",
                  feedback: "This is the average price. But when Angel bought his ticket nine months in advance, he had time to shop around. What effect would that most likely have on his ticket price?",
                  style: {
                    top: "248px",
                    left: "133px",
                    width: "92px",
                    height: "62px",
                  },
                },
                {
                  correct: false,
                  label: "425 dollars",
                  feedback: "$425 is higher than the average price. Since Angel bought his ticket far in advance and had time to shop around, the price he paid is probably lower than average.",
                  style: {
                    top: "190px",
                    left: "211px",
                    width: "77px",
                    height: "53px",
                  },
                },
                {
                  correct: false,
                  label: "600 dollars",
                  feedback: "$600 is much higher than the average price. The person who bought this ticket probably didn't have any better options. Since Angel bought his ticket far in advance and had time to shop around, the price he paid is probably lower than average.",
                  style: {
                    top: "192px",
                    left: "458px",
                    width: "77px",
                    height: "53px",
                  },
                },
                {
                  correct: true,
                  label: "250 dollars",
                  feedback: <>
                    <p>
                      Anyone buying tickets nine months in advance is under no pressure to buy quickly. Knowing that, airlines price the ticket lower to make the sale.
                    </p>
                    <p>
                      <Image
                        width="100%"
                        src={av2}
                        photoCredit={"chain45154/Getty Images"}
                        alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, $425, $600, and $250."
                      />
                    </p>
                  </>,
                  style: {
                    top: "249px",
                    left: "512px",
                    width: "92px",
                    height: "62px",
                  },
                },
              ]}
            >
              <Image
                width="100%"
                src={av1}
                photoCredit={"chain45154/Getty Images"}
                alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, $425, $600, and $250."
              />
            </TaskImageClick>
          </Page>
          <Page>
            <p>
              Lee's meeting in New York City was postponed by three days. He did not want to stay in New York for those extra days, so he decides to find an earlier flight. Because of this change, Lee had to exchange his original ticket for a new ticket a few days before the flight.
            </p>
            <p>
              <b>If the average price of a ticket on this plane is $300, what price do you think Lee paid for his ticket?</b>
            </p>
            <p>
              <i>Select the price that Lee paid in the following image.</i>
            </p>
            <TaskImageClick
              state={Storage()}
              style={{
                paddingBottom: "1em"
              }}
              hotspots={[
                {
                  correct: false,
                  label: "300 dollars",
                  feedback: "This is the average price. However, Lee bought his ticket just three days before take-off, so he didn't have much time to shop around. What effect would that most likely have on his ticket price?",
                  style: {
                    top: "248px",
                    left: "133px",
                    width: "92px",
                    height: "62px",
                  },
                },
                {
                  correct: true,
                  label: "425 dollars",
                  feedback: <>
                    <p>
                      Lee is buying his new ticket at the last minute, and the airline knows this. His demand is inelastic, and therefore the airline charges him a higher price than the average passenger.
                    </p>
                    <p>
                      <Image
                        width="100%"
                        src={av3}
                        photoCredit={"chain45154/Getty Images"}
                        alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, Lee, $600, and Angel"
                      />
                    </p>
                  </>,
                  style: {
                    top: "190px",
                    left: "211px",
                    width: "77px",
                    height: "53px",
                  },
                },
                {
                  correct: false,
                  label: "600 dollars",
                  feedback: "This is double the average cost of a ticket. You're right that Lee will probably pay more than the $300 average ticket price, but he probably has some other flight options, and he can always keep his current ticket and arrive three days early. Which of the other options is higher than average but not as high as double?",
                  style: {
                    top: "192px",
                    left: "458px",
                    width: "77px",
                    height: "53px",
                  },
                },
              ]}
            >
              <Image
                width="100%"
                src={av2}
                photoCredit={"chain45154/Getty Images"}
                alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, $425, $600, and Angel."
              />
            </TaskImageClick>
          </Page>
          <Page>
            <p>
              Hanas has been called to an emergency meeting of her advertising firm in New York City. She was in a huge rush and couldn’t miss this important meeting, so she bought her ticket just 3 hours before take-off.
            </p>
            <p>
              <b>If the average price of a ticket on this plane is $300, what price do you think Hanas paid for her ticket?</b>
            </p>
            <p>
              <i>Select the price that Hanas paid in the following image.</i>
            </p>
            <TaskImageClick
              state={Storage()}
              style={{
                paddingBottom: "1em"
              }}
              hotspots={[
                {
                  correct: false,
                  label: "300 dollars",
                  feedback: "This is the average price. However, Hanas bought her ticket just 3 hours before take-off; she didn't have time to shop around. What effect would that most likely have on her ticket price?",
                  style: {
                    top: "248px",
                    left: "133px",
                    width: "92px",
                    height: "62px",
                  },
                },
                {
                  correct: true,
                  label: "600 dollars",
                  feedback: <>
                    <p>
                      Anyone whose flight is just 3 hours away has few options and inelastic demand. Knowing that, airlines will price the ticket high.
                    </p>
                    <p>
                      <Image
                        width="100%"
                        src={av4}
                        photoCredit={"chain45154/Getty Images"}
                        alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, Lee, Hanas, and Angel."
                      />
                    </p>
                  </>,
                  style: {
                    top: "192px",
                    left: "458px",
                    width: "77px",
                    height: "53px",
                  },
                },
              ]}
            >
              <Image
                width="100%"
                src={av3}
                photoCredit={"chain45154/Getty Images"}
                alt="Looking backwards through the interior of an airplane cabin. There is an aisle in the center with three leather airplane seats on either side. Four seats have the following labels on them: $300, Lee, $600, and Angel."
              />
            </TaskImageClick>
          </Page>
          <Page>
            <p>
              A firm seeks to charge each person the price they are willing and able to pay rather than one market price. This is known as <b>price discrimination</b>. In order to determine the customer's willingness to pay, the firm determines their relative elasticity in various ways. For example, airlines look at the time a passenger has to shop around. If they have a lot of time, it indicates a relatively elastic demand, whereas if they have less time to shop around it would indicate a relatively inelastic demand.
            </p>

            <Row>
              <Col>
                <p align="center">
                  <b>(a) Flat Earth Air: One Price</b>
                </p>
                <p align="center">
                  <Image
                    width="100%"
                    src={panel6a}
                    alt="Two graphs that compare one price and price discrimination. The x axis is quantity of customers and the y axis is price on both graphs. The first graph represents the firm, Flat Earth Air, charging one price. A linear demand line with a negative slope is plotted on the graph. A marginal revenue line also decreases at the steeper slope. A marginal cost line has zero slope and is constant at 100 dollars. The profit is the area of 100 customers times the difference between 300 and 100 dollars. The profit is 20,000 dollars. The second graph is Discriminating Fliers, which represents price discrimination. There is a marginal cost line with zero slope at 100 dollars. A linear demand line with a negative slope extends down from zero customers and 500 dollars and intersects the marginal cost line at 200 customers and 100 dollars. Their profit differs depending on the amount they charge. There are three areas with 5,000 dollars. There is a plus 5,000 dollars area, which is additional revenue from charging 400 dollars. There is a minus 5,000 dollars area, which is revenue lost by not charging 300 dollars. There is another plus 5,000 dollars area, which is additional revenue from charging 200 dollars."
                  />
                </p>
              </Col>
              <Col>
                <p align="center">
                  <b>(b) Discriminating Fliers: Price Discrimination</b>
                </p>
                <p align="center">
                  <Image
                    width="100%"
                    src={panel6b}
                    alt="Two graphs that compare one price and price discrimination. The x axis is quantity of customers and the y axis is price on both graphs. The first graph represents the firm, Flat Earth Air, charging one price. A linear demand line with a negative slope is plotted on the graph. A marginal revenue line also decreases at the steeper slope. A marginal cost line has zero slope and is constant at 100 dollars. The profit is the area of 100 customers times the difference between 300 and 100 dollars. The profit is 20,000 dollars. The second graph is Discriminating Fliers, which represents price discrimination. There is a marginal cost line with zero slope at 100 dollars. A linear demand line with a negative slope extends down from zero customers and 500 dollars and intersects the marginal cost line at 200 customers and 100 dollars. Their profit differs depending on the amount they charge. There are three areas with 5,000 dollars. There is a plus 5,000 dollars area, which is additional revenue from charging 400 dollars. There is a minus 5,000 dollars area, which is revenue lost by not charging 300 dollars. There is another plus 5,000 dollars area, which is additional revenue from charging 200 dollars."
                  />
                </p>              
              </Col>
            </Row>
            {/* <p align="center">
              <Image
                width="100%"
                src={panel6}
                alt="Two graphs that compare one price and price discrimination. The x axis is quantity of customers and the y axis is price on both graphs. The first graph represents the firm, Flat Earth Air, charging one price. A linear demand line with a negative slope is plotted on the graph. A marginal revenue line also decreases at the steeper slope. A marginal cost line has zero slope and is constant at 100 dollars. The profit is the area of 100 customers times the difference between 300 and 100 dollars. The profit is 20,000 dollars. The second graph is Discriminating Fliers, which represents price discrimination. There is a marginal cost line with zero slope at 100 dollars. A linear demand line with a negative slope extends down from zero customers and 500 dollars and intersects the marginal cost line at 200 customers and 100 dollars. Their profit differs depending on the amount they charge. There are three areas with 5,000 dollars. There is a plus 5,000 dollars area, which is additional revenue from charging 400 dollars. There is a minus 5,000 dollars area, which is revenue lost by not charging 300 dollars. There is another plus 5,000 dollars area, which is additional revenue from charging 200 dollars."
              />
            </p> */}
            <TaskMultiChoice
              prompt={<b>Of the two airlines, Flat Earth Air and Discriminating Fliers, which serves fewer customers?</b>}
              key="ffpanel4"
              state={Storage()}
              choices={[
                <span>
                  Flat Earth Air
                </span>,
                <span>
                  Discriminating Fliers
                </span>,
                <span>
                  Both serve the same number of customers
                </span>,
              ]}
              correct={0}
              feedback={[
                <div>
                  Flat Earth Air charges one price to all customers, which means some potential customers whose willingness to pay is above marginal cost but below the single ticket price are not served. As a result, Flat Earth Air ends up serving fewer customers than Discriminating Fliers.
                </div>,
                <div>
                  Discriminating Fliers uses price discrimination, allowing it to offer lower prices to additional customers who would not buy at a single high price. If it can sell to those extra customers, would it end up serving fewer customers than the single-price airline?
                </div>,
                <div>
                  The graphs show that Discriminating Fliers sells tickets to additional customers at lower prices, while Flat Earth Air does not. Because only one airline expands sales to those extra customers, they do not serve the same number of people.
                </div>,
              ]}
            />
          </Page>
        </Navigation>
      </div>
    )
  }
}

export default PriceDiscrimination
